import { Box, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { CommentOutlined, InsertDriveFileOutlined, ExpandLess, ExpandMore, PersonAddOutlined, EditOutlined } from '@mui/icons-material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createCommunicationNote, createDischargeNote, createClaimCorrectionNote, getCreateDischargeNoteButtonStatus, unsubmittedSessionsRequest, createCommunicationNoteForStudent } from '@uplift/api';
import { ROUTING_CONFIG, notesRoute } from '@uplift/constants';
import { useCurrentuser } from '@uplift/contexts';
import { ProviderMedicalRole, SubSystem, UserStatus } from '@uplift/types';
import { Button, useToggleObject, useAlert } from '@uplift/ui-v2';
import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { usePsychiatryReferralFlow } from '../PatientDetails/PsychiatryReferralFlow/usePsychiatryReferralFlow.hook';
import { FinishSubmittingSessionsModal } from './FinishSubmittingSessionsModal';
import { CalendarDateService } from '@uplift/services';
import { useIsActiveMatchBetweenPatientAndProvider, useUserWithProfileQuery } from '@uplift/queries';
import { MatchingFlowContainer } from '../PatientDetails/PsychiatryReferralFlow/MatchingFlow.container';


export type AddActivityButtonProps = {
  patientId: string;
  onUploadDocumentOpen?: () => void;
  disabled?: boolean;
};

export const AddActivityButton: React.VFC<AddActivityButtonProps> = ({ patientId, onUploadDocumentOpen, disabled }) => {
  const me = useCurrentuser();
  const { classes, cx } = useStyles();
  const alert = useAlert();
  const history = useHistory();
  const [ anchor, setAnchor ] = useState<null | HTMLElement>(null);

  const isActiveMatch = useIsActiveMatchBetweenPatientAndProvider(patientId, me.id, { suspense: true });

  const {
    stateId,
    hasActivePsychMatch,
    isPsychiatryMatchingAllowed,
    psychiatryData,
    matchingFetching,
    matchingFlowContainerAvailable,
    matchingProvidersType,
  } = usePsychiatryReferralFlow({ patientId });
  const {
    open: matchingFlowOpen,
    close: matchingFlowClose,
    value: matchingFlowOpened
  } = useToggleObject();
  const {
    open: openFinishSubmittingModal,
    close: closeFinishSubmittingModal,
    value: finishSubmittingModalOpen
  } = useToggleObject();
  const { data: { user, profile } } = useUserWithProfileQuery(patientId);
  const { data: unsubmittedSessions = [], isInitialLoading: isUnsubmittedSessionsLoading } = useQuery([ 'unsubmitted-sessions', patientId ], async () => {
    const response = await unsubmittedSessionsRequest({ patient_id: patientId });

    return response.data;
  });
  const { data: createDischargeNoteEnabled } = useQuery([ 'create-discharge-note-enabled' ], async () => {
    const response = await getCreateDischargeNoteButtonStatus(patientId);

    return response.data;
  });

  const newCommunicationNote = useMutation({
    mutationFn: () => {
      const createDate = CalendarDateService.date().format('YYYY-MM-DD');

      if (user.subsystem === SubSystem.higherEd) {
        return createCommunicationNoteForStudent(patientId, createDate);
      } else {
        return createCommunicationNote(patientId, createDate);
      }
    }
  });
  const newDischargeNote = useMutation({
    mutationFn: () => createDischargeNote(patientId)
  });
  const newClaimCorrectionNote = useMutation({
    mutationFn: () => createClaimCorrectionNote(patientId)
  });

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    setAnchor(e.currentTarget);
  };

  const handleClose = () => {
    setAnchor(null);
  };

  const onCloseFinishSubmittingModal = (proceed: boolean) => {
    closeFinishSubmittingModal();

    if (proceed) {
      history.push(ROUTING_CONFIG.notSubmittedSessions);
    }
  };

  const referralAlert = !hasActivePsychMatch
    ? 'No psychiatry providers available'
    : 'Already matched—check Care Team for more information';

  const activities = useMemo(() => [
    {
      name: 'claim-correction',
      label: 'Add claim correction',
      icon: (
        <Box className={cx(classes.icon, classes.editIcon)}>
          <EditOutlined/>
        </Box>
      ),
      onClick: async () => {
        const noteData = await newClaimCorrectionNote.mutateAsync();

        history.push(notesRoute.createLink({
          patientId: patientId,
          noteType: noteData.data.noteType,
          noteId: noteData.data.id
        }));
      },
      hidden: !me.isImpersonated
    },
    {
      name: 'upload-document',
      label: 'Upload document',
      icon: (
        <Box className={cx(classes.icon, classes.uploadDocumentIcon)}>
          <InsertDriveFileOutlined />
        </Box>
      ),
      onClick: async () => {
        onUploadDocumentOpen?.();
        handleClose();
      }
    },
    {
      name: 'comm-note',
      label: 'Add communication note',
      icon: (
        <Box className={cx(classes.icon, classes.commNoteIcon)}>
          <CommentOutlined />
        </Box>
      ),
      disabled: user.status === UserStatus.initial,
      onDisabledClick: () => {
        alert.info('Client has not finished onboarding');
      },
      onClick: async () => {
        const noteData = await newCommunicationNote.mutateAsync();

        history.push(notesRoute.createLink({
          patientId: patientId,
          noteType: noteData.data.noteType,
          noteId: noteData.data.id
        }));
      },
    },
    {
      name: 'discharge-note',
      label: 'Add discharge note',
      icon: (
        <Box className={cx(classes.icon, classes.commNoteIcon)}>
          <CommentOutlined />
        </Box>
      ),
      onClick: async () => {
        if (unsubmittedSessions.length) {
          handleClose();
          openFinishSubmittingModal();
          return;
        }

        const noteData = await newDischargeNote.mutateAsync();

        history.push(notesRoute.createLink({
          patientId: patientId,
          noteType: noteData.data.noteType,
          noteId: noteData.data.id
        }));
      },
      disabled: !createDischargeNoteEnabled || isUnsubmittedSessionsLoading,
      onDisabledClick: () => {
        alert.info('This client has already been discharged');
      },
    },
    {
      name: 'refer-to-psychiatry',
      label: 'Refer to psychiatry',
      icon: (
        <Box className={cx(classes.icon, classes.referIcon)}>
          <PersonAddOutlined color="secondary" fontSize="small" />
        </Box>
      ),
      onClick: () => matchingFlowOpen(),
      hidden: me.medicalRole !== ProviderMedicalRole.therapist,
      disabled: !isPsychiatryMatchingAllowed || !psychiatryData.length || matchingFetching,
      onDisabledClick: () => {
        alert.info(referralAlert);
      },
    }
  ], [ classes.icon, classes.editIcon, classes.uploadDocumentIcon, classes.commNoteIcon, classes.referIcon, me.isImpersonated, me.medicalRole, user.status, createDischargeNoteEnabled, isUnsubmittedSessionsLoading, isPsychiatryMatchingAllowed, psychiatryData.length, matchingFetching, newClaimCorrectionNote, history, patientId, onUploadDocumentOpen, alert, newCommunicationNote, unsubmittedSessions.length, newDischargeNote, openFinishSubmittingModal, matchingFlowOpen, referralAlert ]);

  return (
    <>
      <FinishSubmittingSessionsModal
        open={finishSubmittingModalOpen}
        onClose={onCloseFinishSubmittingModal}
        unsubmittedSessionsCount={unsubmittedSessions.length}
      />

      <span
        onClick={() => {
          if (!isActiveMatch) {
            alert.info('The client has rematched');
          }
        }}
      >
        <Button
          variant="contained"
          color="secondary"
          endIcon={anchor ? <ExpandLess /> : <ExpandMore />}
          onClick={handleClick}
          aria-controls="add-activity-menu"
          aria-haspopup="true"
          disabled={!isActiveMatch || disabled}
        >
          Add Activity
        </Button>
      </span>
      <Menu
        id="add-activity-menu"
        keepMounted
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {activities.filter(x => !x.hidden).map((x, idx) => (
          <span
            key={idx}
            onClick={() => {
              if (x.disabled) {
                x.onDisabledClick?.();
              }
            }}
          >
            <MenuItem
              className={classes.menuItem}
              onClick={x.onClick}
              disabled={x.disabled}
            >
              <ListItemIcon className={classes.menuItemIcon}>
                {x.icon}
              </ListItemIcon>
              <ListItemText>{x.label}</ListItemText>
            </MenuItem>
          </span>
        ))}
      </Menu>

      {matchingFlowContainerAvailable && (
        <MatchingFlowContainer
          open={matchingFlowOpened}
          onClose={matchingFlowClose}
          stateId={stateId}
          patientName={profile?.preferredName || user.firstName}
          patientId={patientId}
          providers={psychiatryData}
          matchingProvidersType={matchingProvidersType}
        />
      )}
    </>
  );
};

const useStyles = makeStyles({ name: 'AddActivityButton' })(theme => ({
  icon: {
    alignItems: 'center',
    borderRadius: '32px',
    display: 'flex',
    padding: '3px',
    '& svg': {
      fontSize: '18px'
    }
  },
  uploadDocumentIcon: {
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.text.disabled,
  },
  editIcon: {
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.text.disabled,
  },
  commNoteIcon: {
    backgroundColor: theme.palette.lightBlue.background,
    color: theme.palette.lightBlue.main,
  },
  referIcon: {
    backgroundColor: theme.palette.secondary.background,
    color: theme.palette.secondary.main,
  },
  menuItem: {
    gap: '4px'
  },
  menuItemIcon: {
    minWidth: 'unset',
  },
}));
